import React, { useEffect, useMemo, useRef, useState } from 'react'
import { useForm } from 'react-hook-form'
import ManualProcessFields from './components/ManualProcessFields'
import EquipmentAutomationFields from './components/EquipmentAutomationFields'
import Steps, { StepOne, StepTwo } from './components/Steps'
import {
  StepName,
  CalculatorData,
  DesignData,
  CalculatorDataAsNumbers,
} from './types/RoiCalculatorTypes'
import { removePropFromObject } from '../../../helpers/Objects'
import Summary from './components/Summary'
import classnames from 'classnames'
import { User } from '../../../api/Users/Types'
import { Design } from '../../../api/Designs/Types'
import Loading from '../../../components/common/Loading'
import TextField from './components/TextField'
import I18n from '../../../helpers/I18n'
import fromPairs from 'lodash/fromPairs'
import { CalculatorResults, calculateRoiOutput, toNumber } from './helpers/Calculator'
import httpClient from '../../../api/httpClient'
import CalculatorsDesignService from '../../../api/Tools/Calculators/Design/Service'

const getExpandedSteps = value => ({
  [StepName.one]: value,
  [StepName.two]: value,
})

interface Props {
  exchangeRate: ExchangeRate
  currentUser?: User
  localizedPath: string
}

const DesignFormDefaultValues: DesignData = {
  designNumber: '',
}

const CalculatorFormDefaultValues: CalculatorData = {
  hoursPerShift: '8',
  workDaysPerWeek: '5',
  shiftsPerDay: '1',
  numberOfWorkersPerShift: '1',
  labourRate: '',
  estimatedWorkerEfficiency: '0.8',
  manualProcess: '',
  equipmentHardwareCost: '',
  estimatedEquipmentEfficiency: '0.9',
  automatedProcess: '',
}

const RoiCalculator: React.FC<Props> = ({ exchangeRate, currentUser, localizedPath }) => {
  const urlParams = new URLSearchParams(window.location.search)
  const [calculatorData, setCalculatorData] = useState<CalculatorData>({})
  const [calculatorResults, setCalculatorResults] = useState<CalculatorResults | []>([])
  const [designSignInErrorMessage, setDesignSignInErrorMessage] = useState<string>('')
  const [designResponseErrorMessage, setDesignResponseErrorMessage] = useState<string>('')
  const [design, setDesign] = useState<Design | undefined>()
  const [isLoadingDesign, setIsLoadingDesign] = useState<boolean>(false)
  const [isLoadingResults, setIsLoadingResults] = useState<boolean>(false)

  const designFormControl = useForm({
    defaultValues: DesignFormDefaultValues,
  })

  const formControl = useForm({
    defaultValues: CalculatorFormDefaultValues,
  })
  const watchedFields = formControl.watch()

  const stepOneRef = useRef<HTMLDivElement>(null)
  const stepTwoRef = useRef<HTMLDivElement>(null)
  const summaryRef = useRef<HTMLDivElement>(null)

  const [expandedSteps, setExpandedSteps] = useState(getExpandedSteps(true))

  const calculateCountRef = useRef(0)

  const calculatorDataAsNumbers = useMemo(
    () =>
      fromPairs(
        Object.entries(calculatorData).map(([key, value]) => [key, toNumber(value)])
      ) as Required<CalculatorDataAsNumbers>,
    [calculatorData]
  )

  const toggleStep = (stepName: StepName) =>
    setExpandedSteps(currentExpandedSteps => ({
      ...currentExpandedSteps,
      [stepName]: !currentExpandedSteps[stepName],
    }))

  const resetCalculatorData = () => {
    setCalculatorData({})
    formControl.setValue('equipmentHardwareCost', undefined)
  }

  const submitDesignForm = async (data): Promise<void> => {
    setIsLoadingDesign(true)

    if (data.designNumber === '') {
      setDesign(undefined)
      setIsLoadingDesign(false)
      return
    }

    const designNumberString = data.designNumber || urlParams.get('designNumber')
    const designId = designNumberString?.split('-').pop()

    try {
      const design = await CalculatorsDesignService.fetchDesign(designId)
      setDesign(design)
      setIsLoadingDesign(false)
    } catch (error) {
      setDesign(undefined)
      setDesignResponseErrorMessage(error.message)
      setIsLoadingDesign(false)
    }
  }

  const designFormSubmitHandler = (data): void => {
    resetCalculatorData()
    submitDesignForm(data)
  }

  const adjustData = (data: CalculatorData) => {
    if (data.manualProcess === '') {
      data.manualProcess = 0
    }
    if (data.automatedProcess === '') {
      data.automatedProcess = 0
    }
    const emptyKeys = Object.entries(data)
      .filter(([_, value]) => value === '')
      .map(([key, _]) => key)
    const adjustedData = removePropFromObject(data, emptyKeys)
    return adjustedData
  }

  const resetDesignFormErrors = (): void => {
    setDesignSignInErrorMessage('')
    setDesignResponseErrorMessage('')
  }

  const calculatorFormSubmitHandler = (data: CalculatorData) => {
    setIsLoadingResults(true)
    setCalculatorData(currentData => ({ ...currentData, ...adjustData(data) }))
    setExpandedSteps(getExpandedSteps(true))
    setIsLoadingResults(false)
  }

  const calculatorFormSubmitHandlerWithRequest = (data: CalculatorData) => {
    calculateCountRef.current++
    calculatorFormSubmitHandler(data)
  }

  const onError = () => {
    setExpandedSteps(getExpandedSteps(true))
    setIsLoadingResults(false)
  }

  const setFormFieldFromParam = (formControl, paramKey: string): void => {
    const paramValue = urlParams.get(paramKey)

    if (paramValue) {
      formControl.setValue(paramKey, paramValue)
    }
  }

  const populateFormWithUrlParams = (): void => {
    const autoSubmitParam = urlParams.get('autoSubmit')
    const designNumber = urlParams.get('designNumber')

    resetCalculatorData()

    setFormFieldFromParam(designFormControl, 'designNumber')

    Object.keys(watchedFields).forEach(fieldKey => {
      setFormFieldFromParam(formControl, fieldKey)
    })

    if (autoSubmitParam) {
      if (designNumber) {
        submitDesignForm(designFormControl.getValues()).then(() => {
          calculatorFormSubmitHandler(formControl.getValues())
        })
      } else {
        calculatorFormSubmitHandler(formControl.getValues())
      }
    }
  }

  useEffect(() => {
    formControl.clearErrors('automatedProcess')
    formControl.clearErrors('manualProcess')
  }, [watchedFields?.manualProcess, watchedFields?.automatedProcess])

  useEffect(() => {
    populateFormWithUrlParams()
  }, [])

  useEffect(() => {
    const results = calculateRoiOutput(calculatorDataAsNumbers)
    setCalculatorResults(results)
  }, [calculatorDataAsNumbers])

  // when the calculatorCountRef is at 1, that's when we can be sure
  // that the form has been submitted and the results have been calculated.
  useEffect(() => {
    if (calculateCountRef.current === 1) {
      httpClient
        .post(localizedPath, {
          // get the rate and currency code from exchange rate
          exchange_rate: {
            rate: exchangeRate.rate,
            currency_code: exchangeRate.currency_code,
          },
          calculator_data: calculatorData,
          calculator_results: {
            totalPaybackTime: calculatorResults[0],
            unitsToBreakEven: calculatorResults[1],
            fiveYearSavings: calculatorResults[2],
            cumulativeValues: calculatorResults[3],
            timeToBreakEvenInYears: calculatorResults[4],
          },
        })
        .then(() => {
          calculateCountRef.current = 0 // reset the count
        })
    }
  }, [calculatorResults])

  const renderSteps = () => {
    return (
      <Steps>
        <h2 className='roi-calculator-heading'>
          {I18n.t('views.roi_calculator.calculator_heading')}
        </h2>
        <form
          onSubmit={designFormControl.handleSubmit(designFormSubmitHandler, onError)}
          onChange={resetDesignFormErrors}
          className='roi-calculator-design-form'
        >
          <TextField
            formControl={designFormControl}
            name='designNumber'
            label={[
              I18n.t('views.roi_calculator.form.labels.design_id'),
              <em className='roi-calculator-optional' key='design-id'>
                ({I18n.t('views.roi_calculator.form.labels.optional')})
              </em>,
            ]}
            errorMessage={designSignInErrorMessage || designResponseErrorMessage}
            infoTip={I18n.t('views.roi_calculator.form.tooltips.design_id')}
          />
          <button
            type='submit'
            className={classnames('roi-calculator-design-submit-button', {
              'design-response-error': !!designResponseErrorMessage,
              'design-sign-in-error': !!designSignInErrorMessage,
              'design-sign-in-error-international-locale':
                I18n.locale === 'fr' || I18n.locale === 'de',
            })}
          >
            {isLoadingDesign ? <Loading /> : I18n.t('views.roi_calculator.form.actions.submit')}
          </button>
        </form>
        <form
          onSubmit={formControl.handleSubmit(calculatorFormSubmitHandlerWithRequest, onError)}
          className='roi-calculator-form'
        >
          <StepOne
            ref={stepOneRef}
            className={classnames({
              'roi-calculator-step-collapsed': !expandedSteps[StepName.one],
            })}
            subTitle={
              <button
                className='button-blank'
                type='button'
                onClick={() => toggleStep(StepName.one)}
              >
                <i className='far fa-chevron-down' aria-expanded={expandedSteps[StepName.one]}></i>
              </button>
            }
          >
            <ManualProcessFields
              formControl={formControl}
              watchedFields={watchedFields}
              exchangeRate={exchangeRate}
            />
          </StepOne>
          <StepTwo
            ref={stepTwoRef}
            className={classnames({
              'roi-calculator-step-collapsed': !expandedSteps[StepName.two],
            })}
            subTitle={
              <button
                className='button-blank'
                type='button'
                onClick={() => toggleStep(StepName.two)}
              >
                <i className='far fa-chevron-down' aria-expanded={expandedSteps[StepName.two]}></i>
              </button>
            }
          >
            <EquipmentAutomationFields
              formControl={formControl}
              exchangeRate={exchangeRate}
              design={design}
              watchedFields={watchedFields}
            />
            <button className='roi-calculator-submit-button' type='submit'>
              {isLoadingResults ? (
                <Loading />
              ) : (
                I18n.t('views.roi_calculator.form.actions.calculate')
              )}
            </button>
          </StepTwo>
        </form>
      </Steps>
    )
  }

  const hasValues = useMemo(
    () => !!Object.values(adjustData(calculatorData)).length,
    [calculatorData]
  )

  return (
    <div
      className='roi-calculator'
      {...(hasValues && { 'data-controller': 'prompt-before-leaving' })}
    >
      <div className='roi-calculator-container'>
        {renderSteps()}
        <Summary
          ref={summaryRef}
          exchangeRate={exchangeRate}
          design={design}
          localizedPath={localizedPath}
          calculatorDataAsNumbers={calculatorDataAsNumbers}
          calculatorResults={calculatorResults}
        />
      </div>
    </div>
  )
}

export default RoiCalculator
